import React, { useState } from 'react';
import { Card, notification } from 'antd';
import OnboardingStartScreen from './OnboardingStartScreen';
import AddNewWebsiteForm from './AddNewWebsiteForm';
import DomainSetupProgress from './DomainSetupProgress';
import { useCreateDomainSetupProcessMutation } from '../../process/service';
import { AddNewWebsiteIndexEntryParams } from '../types';


import { useAddTrackedKeywordsMutation } from '../../../trackedKeywords/service';
import { useAddWebsiteIndexEntryMutation, useStartOnboardingMutation } from '../service';
import { useAddSitemapImportProcessMutation, useAddDomainAnalysisProcessMutation } from '../..//process/service';
import { useCreateBackgroundAnalysisReportMutation } from '../../../analysis/service';
import useGetCurrentWebsite from '../hooks/useGetCurrentWebsite';
import useStartDomainAiAnalyses from '../../../llmAnalysis/hooks/useStartDomainAiAnalyses';
import './AddNewWebsite.less';
import { useSendLoadingTimeRequestMutation } from '../../../siteSpeed/service';


type Props = {
    fromOnboarding: boolean;
    onFinish: () => void;
};


export default function AddNewWebsiteIndexEntry({ fromOnboarding, onFinish }: Props) {
    const [inputDomain, setInputDomain] = useState<string | null>(null);
    const [createDomainSetupProcess] = useCreateDomainSetupProcessMutation();


    const { setSelectedWebsite } = useGetCurrentWebsite();
    const [addWebsiteIndexEntry] = useAddWebsiteIndexEntryMutation();
    const [addTrackedKeywords] = useAddTrackedKeywordsMutation();
    const [addSitemapImportProcess] = useAddSitemapImportProcessMutation();
    const [addDomainAnalysisProcess] = useAddDomainAnalysisProcessMutation();
    const [createBackgroundAnalysis] = useCreateBackgroundAnalysisReportMutation();
    const [startOnboarding] = useStartOnboardingMutation();
    const [sendLoadingTimeRequest] = useSendLoadingTimeRequestMutation();
    const startDomainAiAnalyses = useStartDomainAiAnalyses();

    const onValidDomain = (domain: string) => {
        createDomainSetupProcess({ domain })
            .unwrap()
            .then(() => {
                setInputDomain(domain);
            })
            .catch((error) => {
                console.error("Failed to create domain setup process:", error);
            });
    };

    const handleAddNewWebsite = async (params: AddNewWebsiteIndexEntryParams, keywords: string[]) => {
        try {
            let newWebsiteIndexEntry;

            try {
                newWebsiteIndexEntry = await addWebsiteIndexEntry({ domain: params.domain, homepage_url: params.homepage, settings: params.settings }).unwrap();
            } catch (error) {
                notification.error({
                    message: "Something went wrong!",
                    description: `There was an error adding ${params.domain}. Please try again or contact support!`,
                });
                return;
            }

            // Started the moment the domain has a guid, and deliberately not waited for: the customer
            // opens these reports from the menu rather than from here, so what matters is that they are
            // already running by then.
            //
            // Only for the website a free trial opens on. Every website added anywhere else was
            // paying for five analyses - a visibility report for each assistant, the AI content
            // report and the visual SEO report - the moment it was added, whether or not anybody
            // ever opened one of them, and each of those is charged per run. Elsewhere the report
            // starts its own analysis when it is opened and finds none stored, so all that is given
            // up is that the customer waits through that first run instead of finding it done.
            //
            // A trial is the one case worth paying for up front: the customer is deciding whether
            // to buy, and an empty report is what they would be deciding against.
            //
            // `fromOnboarding` says which route the customer arrived by, not what their
            // subscription is. Only the account-setup page sets it, and a customer reaches that
            // page straight after starting a trial, so it stands in for "a trial customer's first
            // website" rather than checking it.
            if (fromOnboarding) {
                startDomainAiAnalyses({
                    wGuid: newWebsiteIndexEntry.guid,
                    domain: newWebsiteIndexEntry.domain,
                    homepageUrl: newWebsiteIndexEntry.homepage_url,
                });
            }

            try {
                // send loading time request
                await sendLoadingTimeRequest({ url: params.homepage, regions: params.settings.regions }).unwrap();
            } catch (err) {
                console.error("Failed to send loading time request:", err);
            }

            if (newWebsiteIndexEntry) {
                try {
                    // start sitemap import
                    if (newWebsiteIndexEntry.sitemap_urls.length > 0) {
                        await addSitemapImportProcess({ w_guid: newWebsiteIndexEntry.guid, sitemap_urls: newWebsiteIndexEntry.sitemap_urls }).unwrap();
                    }

                    // start site-wide factors analysis
                    await addDomainAnalysisProcess({ w_guid: newWebsiteIndexEntry.guid }).unwrap();

                    // start a full legacy seo-audit
                    const source = fromOnboarding ? "onboarding_wizard" : "add_tracked_website_wizard";
                    await createBackgroundAnalysis({ url: params.homepage, competitors: params.settings.competitors, source }).unwrap();

                    // Only the account-setup route reaches this with fromOnboarding set, so this is
                    // what marks the one website an organization's onboarding emails are about.
                    // Losing the record costs that organization those emails, which is preferred to
                    // failing the rest of the setup in front of them.
                    if (fromOnboarding) {
                        try {
                            await startOnboarding({ w_guid: newWebsiteIndexEntry.guid }).unwrap();
                        } catch (error) {
                            console.error("Failed to start onboarding:", error);
                        }
                    }

                    // add tracked keywords
                    await addTrackedKeywords({
                        domain: params.domain,
                        locationName: params.settings.location_name,
                        languageCode: params.settings.language_code,
                        keywords: keywords,
                    }).unwrap();

                    setSelectedWebsite(newWebsiteIndexEntry.guid);
                } catch (_) {
                    notification.error({ message: "Something went wrong!", description: "Please try again or contact support!" });
                }
            }
        } catch (error) {
        } finally {
            onFinish();
        }
    };

    return (
        <Card bordered={false} className="add-website-index-entry-card">
            {
                inputDomain === null ? (
                    fromOnboarding ? <OnboardingStartScreen onValidDomain={onValidDomain} /> : <AddNewWebsiteForm onValidDomain={onValidDomain} title={"Add a new website to start SEO monitoring.<br /> After setup, we'll start a <strong>full SEO analysis</strong> to identify issues and deliver clear <strong>how-to-fix</strong> guidance."} />
                ) : <DomainSetupProgress domain={inputDomain} onSetupFinish={handleAddNewWebsite} />
            }
        </Card>
    );
}
